import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  Stack,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  Close,
  Save,
  Cancel,
  Business,
  Settings,
  Group,
  Add,
  Remove,
} from '@mui/icons-material'

import { AppDispatch, RootState } from '../../store'
import { updateProject, fetchProjects } from '../../store/slices/projectSlice'
import { fetchAllUsers } from '../../store/slices/userSlice'
import { getInitials } from '../../utils/userColors'
import { Project, ProjectMember } from '../../types'

interface EditProjectDialogProps {
  open: boolean
  onClose: () => void
  project: Project | null
}

interface ProjectForm {
  name: string
  description: string
  key: string
  type: 'software' | 'business' | 'service_desk'
  status: 'active' | 'archived' | 'draft'
  lead: string
  members: ProjectMember[]
}

const EditProjectDialog: React.FC<EditProjectDialogProps> = ({ open, onClose, project }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { loading } = useSelector((state: RootState) => state.projects)
  const { users } = useSelector((state: RootState) => state.users)
  const { user } = useSelector((state: RootState) => state.auth)
  
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberRole, setNewMemberRole] = useState<'admin' | 'member' | 'viewer'>('member')

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProjectForm>()

  const watchedLead = watch('lead')

  useEffect(() => {
    if (project && open) {
      // Populate form with project data
      setValue('name', project.name)
      setValue('description', project.description || '')
      setValue('key', project.key)
      setValue('type', project.type)
      setValue('status', project.status)
      setValue('lead', project.lead._id)
      setValue('members', project.members)
    }
  }, [project, open, setValue])

  // Load users if not already loaded (only for admins)
  useEffect(() => {
    if (users.length === 0 && user?.role === 'admin') {
      dispatch(fetchAllUsers())
    }
  }, [dispatch, users.length, user?.role])

  const onSubmit = async (data: ProjectForm) => {
    if (!project) return

    try {
      const result = await dispatch(updateProject({
        projectId: project._id,
        projectData: data
      }))
      
      if (updateProject.fulfilled.match(result)) {
        // Refresh the projects list to ensure updated data is reflected
        await dispatch(fetchProjects({}))
        handleClose()
      }
    } catch (error) {
      console.error('Error updating project:', error)
    }
  }

  const handleClose = () => {
    reset()
    setNewMemberEmail('')
    setNewMemberRole('member')
    onClose()
  }

  const handleAddMember = () => {
    if (!newMemberEmail.trim()) return

    const user = users.find(u => u.email === newMemberEmail.trim() && u.isActive !== false)
    if (!user) {
      alert('Active user not found with this email')
      return
    }

    const currentMembers = watch('members') || []
    const isAlreadyMember = currentMembers.some(m => 
      (typeof m.user === 'string' ? m.user : m.user._id) === user._id
    )

    if (isAlreadyMember) {
      alert('User is already a member of this project')
      return
    }

    const newMember: ProjectMember = {
      user: user._id,
      role: newMemberRole,
      joinedAt: new Date().toISOString()
    }

    setValue('members', [...currentMembers, newMember])
    setNewMemberEmail('')
    setNewMemberRole('member')
  }

  const handleRemoveMember = (memberId: string) => {
    const currentMembers = watch('members') || []
    const updatedMembers = currentMembers.filter(m => 
      (typeof m.user === 'string' ? m.user : m.user._id) !== memberId
    )
    setValue('members', updatedMembers)
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error'
      case 'member': return 'primary'
      case 'viewer': return 'default'
      default: return 'default'
    }
  }

  if (!project) return null

  const currentMembers = watch('members') || []
  const activeUsers = users.filter(user => user.isActive !== false)
  const availableUsers = activeUsers.filter(user => 
    user._id !== watchedLead && 
    !currentMembers.some(m => (typeof m.user === 'string' ? m.user : m.user._id) === user._id)
  )
  // Keep current lead visible even if inactive, but only offer active users for new selection
  const leadOptions = (() => {
    const currentLead = users.find(u => u._id === watchedLead)
    if (currentLead && currentLead.isActive === false && !activeUsers.some(u => u._id === currentLead._id)) {
      return [currentLead, ...activeUsers]
    }
    return activeUsers
  })()

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Project: {project.name}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Basic Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business fontSize="small" />
                    Basic Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...register('name', { required: 'Project name is required' })}
                        fullWidth
                        label="Project Name"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...register('key', { 
                          required: 'Project key is required',
                          pattern: {
                            value: /^[A-Z]+$/,
                            message: 'Project key must be uppercase letters only'
                          }
                        })}
                        fullWidth
                        label="Project Key"
                        error={!!errors.key}
                        helperText={errors.key?.message}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        {...register('description')}
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        placeholder="Describe your project..."
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="type"
                        control={control}
                        rules={{ required: 'Project type is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.type}>
                            <InputLabel>Project Type</InputLabel>
                            <Select {...field} label="Project Type">
                              <MenuItem value="software">Software</MenuItem>
                              <MenuItem value="business">Business</MenuItem>
                              <MenuItem value="service_desk">Service Desk</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="status"
                        control={control}
                        rules={{ required: 'Project status is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.status}>
                            <InputLabel>Status</InputLabel>
                            <Select {...field} label="Status">
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="archived">Archived</MenuItem>
                              <MenuItem value="draft">Draft</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Team Management */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Group fontSize="small" />
                    Team Management
                  </Typography>
                  
                  {/* Project Lead */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                      Project Lead
                    </Typography>
                    <Controller
                      name="lead"
                      control={control}
                      rules={{ required: 'Project lead is required' }}
                      render={({ field }) => (
                        <Autocomplete
                          options={leadOptions}
                          getOptionLabel={(user) => user.name}
                          value={users.find(u => u._id === field.value) || null}
                          onChange={(_, value) => field.onChange(value?._id)}
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              error={!!errors.lead}
                              helperText={errors.lead?.message}
                              placeholder="Select project lead"
                            />
                          )}
                          renderOption={(props, user) => (
                            <li {...props}>
                              <Avatar src={user.avatar} sx={{ width: 24, height: 24, mr: 1 }}>
                                {getInitials(user.name, '?', false)}
                              </Avatar>
                              {user.name} ({user.email})
                            </li>
                          )}
                        />
                      )}
                    />
                  </Box>

                  {/* Add New Member */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Add Team Member
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={availableUsers}
                          getOptionLabel={(user) => `${user.name} (${user.email})`}
                          value={null}
                          onChange={(_, value) => setNewMemberEmail(value?.email || '')}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select user to add" />
                          )}
                          renderOption={(props, user) => (
                            <li {...props}>
                              <Avatar src={user.avatar} sx={{ width: 24, height: 24, mr: 1 }}>
                                {getInitials(user.name, '?', false)}
                              </Avatar>
                              {user.name} ({user.email})
                            </li>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={newMemberRole}
                            label="Role"
                            onChange={(e) => setNewMemberRole(e.target.value as any)}
                          >
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="member">Member</MenuItem>
                            <MenuItem value="viewer">Viewer</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={handleAddMember}
                          disabled={!newMemberEmail}
                          fullWidth
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Current Members */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Current Team Members ({currentMembers.length})
                    </Typography>
                    <List>
                      {currentMembers.map((member, index) => {
                        const user = users.find(u => u._id === (typeof member.user === 'string' ? member.user : member.user._id))
                        if (!user) return null
                        
                        return (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar src={user.avatar}>
                                {getInitials(user.name, '?', false)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={user.name}
                              secondary={user.email}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={member.role} 
                                size="small" 
                                color={getRoleColor(member.role) as any}
                              />
                              {user._id !== watchedLead && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveMember(user._id)}
                                  color="error"
                                >
                                  <Remove />
                                </IconButton>
                              )}
                            </Box>
                          </ListItem>
                        )
                      })}
                    </List>
                    
                    {currentMembers.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          No team members added yet
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Project Settings */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Settings fontSize="small" />
                    Project Info
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Project Key
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {project.key}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {new Date(project.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Last Updated
                      </Typography>
                      <Typography variant="body2">
                        {new Date(project.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          loading={loading}
          startIcon={<Save />}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditProjectDialog
